import type { TranslationTable } from "@repo/shared/types";

type EventText = { label: string; description: string };

type Table = {
  title: string;
  description: string;
  saveFailed: string;
  loadFailed: string;
  events: {
    signatureRequested: EventText;
    signed: EventText;
    signatureRejected: EventText;
    signatureExpired: EventText;
    submittedByClient: EventText;
    completed: EventText;
    complianceDecided: EventText;
    supportReply: EventText;
  };
};

// All 17 locales: a personal settings page every user can open.
export const T: TranslationTable<Table> = {
  en_GB: {
    title: "Notifications",
    description:
      "Choose what you want to be told about. While Quantfolio is open you see it here; otherwise it goes to your phone through the Quantfolio app.",
    saveFailed: "Could not save the notification setting",
    loadFailed: "Could not load your notification settings",
    events: {
      signatureRequested: {
        label: "Sent for signing",
        description: "One of your flows is sent to the client for signing.",
      },
      signed: { label: "Signed", description: "The client has signed one of your flows." },
      signatureRejected: {
        label: "Signing rejected",
        description: "The client rejected signing one of your flows.",
      },
      signatureExpired: {
        label: "Signing expired",
        description: "A signing request for one of your flows expired before it was signed.",
      },
      submittedByClient: {
        label: "Submitted by the client",
        description: "The client submitted one of your flows from their link.",
      },
      completed: { label: "Flow completed", description: "One of your flows is completed." },
      complianceDecided: {
        label: "Compliance decision",
        description: "Compliance approved or rejected one of your flows.",
      },
      supportReply: {
        label: "Support replies",
        description: "Someone replied on a support ticket you created.",
      },
    },
  },
  fr_FR: {
    title: "Notifications",
    description:
      "Choisissez ce dont vous voulez être informé. Lorsque Quantfolio est ouvert, vous le voyez ici ; sinon, la notification est envoyée sur votre téléphone via l'application Quantfolio.",
    saveFailed: "Impossible d'enregistrer le paramètre de notification",
    loadFailed: "Impossible de charger vos paramètres de notification",
    events: {
      signatureRequested: {
        label: "Envoyé pour signature",
        description: "Un de vos parcours est envoyé au client pour signature.",
      },
      signed: { label: "Signé", description: "Le client a signé un de vos parcours." },
      signatureRejected: {
        label: "Signature refusée",
        description: "Le client a refusé de signer un de vos parcours.",
      },
      signatureExpired: {
        label: "Signature expirée",
        description:
          "Une demande de signature pour un de vos parcours a expiré avant d'être signée.",
      },
      submittedByClient: {
        label: "Soumis par le client",
        description: "Le client a soumis un de vos parcours depuis son lien.",
      },
      completed: { label: "Parcours terminé", description: "Un de vos parcours est terminé." },
      complianceDecided: {
        label: "Décision de conformité",
        description: "La conformité a approuvé ou rejeté un de vos parcours.",
      },
      supportReply: {
        label: "Réponses de l'assistance",
        description: "Quelqu'un a répondu à un ticket d'assistance que vous avez créé.",
      },
    },
  },
  es_ES: {
    title: "Notificaciones",
    description:
      "Elija sobre qué quiere recibir avisos. Mientras Quantfolio está abierto lo verá aquí; si no, le llegará al teléfono a través de la aplicación Quantfolio.",
    saveFailed: "No se pudo guardar el ajuste de notificación",
    loadFailed: "No se pudieron cargar sus ajustes de notificaciones",
    events: {
      signatureRequested: {
        label: "Enviado para firma",
        description: "Uno de sus flujos se envía al cliente para su firma.",
      },
      signed: { label: "Firmado", description: "El cliente ha firmado uno de sus flujos." },
      signatureRejected: {
        label: "Firma rechazada",
        description: "El cliente rechazó firmar uno de sus flujos.",
      },
      signatureExpired: {
        label: "Firma caducada",
        description: "Una solicitud de firma de uno de sus flujos caducó antes de firmarse.",
      },
      submittedByClient: {
        label: "Enviado por el cliente",
        description: "El cliente envió uno de sus flujos desde su enlace.",
      },
      completed: { label: "Flujo completado", description: "Uno de sus flujos se ha completado." },
      complianceDecided: {
        label: "Decisión de cumplimiento",
        description: "Cumplimiento aprobó o rechazó uno de sus flujos.",
      },
      supportReply: {
        label: "Respuestas de soporte",
        description: "Alguien respondió en un ticket de soporte que usted creó.",
      },
    },
  },
  pt_BR: {
    title: "Notificações",
    description:
      "Escolha sobre o que você quer ser avisado. Enquanto o Quantfolio estiver aberto, você verá aqui; caso contrário, o aviso vai para o seu telefone pelo aplicativo Quantfolio.",
    saveFailed: "Não foi possível salvar a configuração de notificação",
    loadFailed: "Não foi possível carregar suas configurações de notificação",
    events: {
      signatureRequested: {
        label: "Enviado para assinatura",
        description: "Um dos seus fluxos é enviado ao cliente para assinatura.",
      },
      signed: { label: "Assinado", description: "O cliente assinou um dos seus fluxos." },
      signatureRejected: {
        label: "Assinatura recusada",
        description: "O cliente recusou assinar um dos seus fluxos.",
      },
      signatureExpired: {
        label: "Assinatura expirada",
        description:
          "Uma solicitação de assinatura de um dos seus fluxos expirou antes de ser assinada.",
      },
      submittedByClient: {
        label: "Enviado pelo cliente",
        description: "O cliente enviou um dos seus fluxos pelo link dele.",
      },
      completed: { label: "Fluxo concluído", description: "Um dos seus fluxos foi concluído." },
      complianceDecided: {
        label: "Decisão do compliance",
        description: "O compliance aprovou ou rejeitou um dos seus fluxos.",
      },
      supportReply: {
        label: "Respostas do suporte",
        description: "Alguém respondeu em um chamado de suporte que você criou.",
      },
    },
  },
  de_DE: {
    title: "Benachrichtigungen",
    description:
      "Wählen Sie, worüber Sie informiert werden möchten. Solange Quantfolio geöffnet ist, sehen Sie es hier; sonst geht es über die Quantfolio-App an Ihr Telefon.",
    saveFailed: "Die Benachrichtigungseinstellung konnte nicht gespeichert werden",
    loadFailed: "Ihre Benachrichtigungseinstellungen konnten nicht geladen werden",
    events: {
      signatureRequested: {
        label: "Zur Unterschrift gesendet",
        description: "Einer Ihrer Abläufe wird dem Kunden zur Unterschrift gesendet.",
      },
      signed: {
        label: "Unterschrieben",
        description: "Der Kunde hat einen Ihrer Abläufe unterschrieben.",
      },
      signatureRejected: {
        label: "Unterschrift abgelehnt",
        description: "Der Kunde hat die Unterschrift eines Ihrer Abläufe abgelehnt.",
      },
      signatureExpired: {
        label: "Unterschrift abgelaufen",
        description:
          "Eine Unterschriftsanfrage für einen Ihrer Abläufe ist abgelaufen, bevor sie unterschrieben wurde.",
      },
      submittedByClient: {
        label: "Vom Kunden eingereicht",
        description: "Der Kunde hat einen Ihrer Abläufe über seinen Link eingereicht.",
      },
      completed: {
        label: "Ablauf abgeschlossen",
        description: "Einer Ihrer Abläufe ist abgeschlossen.",
      },
      complianceDecided: {
        label: "Compliance-Entscheidung",
        description: "Compliance hat einen Ihrer Abläufe genehmigt oder abgelehnt.",
      },
      supportReply: {
        label: "Support-Antworten",
        description: "Jemand hat auf ein von Ihnen erstelltes Support-Ticket geantwortet.",
      },
    },
  },
  it_IT: {
    title: "Notifiche",
    description:
      "Scegli di cosa vuoi essere avvisato. Mentre Quantfolio è aperto lo vedi qui; altrimenti arriva sul tuo telefono tramite l'app Quantfolio.",
    saveFailed: "Impossibile salvare l'impostazione di notifica",
    loadFailed: "Impossibile caricare le tue impostazioni di notifica",
    events: {
      signatureRequested: {
        label: "Inviato per la firma",
        description: "Uno dei tuoi flussi viene inviato al cliente per la firma.",
      },
      signed: { label: "Firmato", description: "Il cliente ha firmato uno dei tuoi flussi." },
      signatureRejected: {
        label: "Firma rifiutata",
        description: "Il cliente ha rifiutato di firmare uno dei tuoi flussi.",
      },
      signatureExpired: {
        label: "Firma scaduta",
        description: "Una richiesta di firma per uno dei tuoi flussi è scaduta prima della firma.",
      },
      submittedByClient: {
        label: "Inviato dal cliente",
        description: "Il cliente ha inviato uno dei tuoi flussi dal suo link.",
      },
      completed: {
        label: "Flusso completato",
        description: "Uno dei tuoi flussi è stato completato.",
      },
      complianceDecided: {
        label: "Decisione della compliance",
        description: "La compliance ha approvato o respinto uno dei tuoi flussi.",
      },
      supportReply: {
        label: "Risposte dell'assistenza",
        description: "Qualcuno ha risposto a un ticket di assistenza che hai creato.",
      },
    },
  },
  ar: {
    title: "الإشعارات",
    description:
      "اختر ما تريد أن تُبلَّغ به. عندما يكون Quantfolio مفتوحًا تراه هنا، وإلا يصل إلى هاتفك عبر تطبيق Quantfolio.",
    saveFailed: "تعذّر حفظ إعداد الإشعار",
    loadFailed: "تعذّر تحميل إعدادات الإشعارات",
    events: {
      signatureRequested: {
        label: "أُرسل للتوقيع",
        description: "أُرسل أحد مساراتك إلى العميل للتوقيع.",
      },
      signed: { label: "تم التوقيع", description: "وقّع العميل أحد مساراتك." },
      signatureRejected: {
        label: "رُفض التوقيع",
        description: "رفض العميل توقيع أحد مساراتك.",
      },
      signatureExpired: {
        label: "انتهت صلاحية التوقيع",
        description: "انتهت صلاحية طلب توقيع لأحد مساراتك قبل توقيعه.",
      },
      submittedByClient: {
        label: "أرسله العميل",
        description: "أرسل العميل أحد مساراتك من رابطه.",
      },
      completed: { label: "اكتمل المسار", description: "اكتمل أحد مساراتك." },
      complianceDecided: {
        label: "قرار الامتثال",
        description: "وافق قسم الامتثال على أحد مساراتك أو رفضه.",
      },
      supportReply: {
        label: "ردود الدعم",
        description: "ردّ أحدهم على تذكرة دعم أنشأتها.",
      },
    },
  },
  ja_JP: {
    title: "通知",
    description:
      "通知を受け取る内容を選択してください。Quantfolio を開いている間はここに表示され、それ以外は Quantfolio アプリを通じてスマートフォンに届きます。",
    saveFailed: "通知設定を保存できませんでした",
    loadFailed: "通知設定を読み込めませんでした",
    events: {
      signatureRequested: {
        label: "署名依頼",
        description: "担当フローが顧客に署名依頼されたとき。",
      },
      signed: { label: "署名済み", description: "顧客が担当フローに署名したとき。" },
      signatureRejected: {
        label: "署名拒否",
        description: "顧客が担当フローの署名を拒否したとき。",
      },
      signatureExpired: {
        label: "署名期限切れ",
        description: "担当フローの署名依頼が署名前に期限切れになったとき。",
      },
      submittedByClient: {
        label: "顧客による提出",
        description: "顧客がリンクから担当フローを提出したとき。",
      },
      completed: { label: "フロー完了", description: "担当フローが完了したとき。" },
      complianceDecided: {
        label: "コンプライアンス判断",
        description: "コンプライアンスが担当フローを承認または却下したとき。",
      },
      supportReply: {
        label: "サポートからの返信",
        description: "作成したサポートチケットに返信があったとき。",
      },
    },
  },
  hi_IN: {
    title: "सूचनाएँ",
    description:
      "चुनें कि आप किन बातों की सूचना चाहते हैं। Quantfolio खुला होने पर आप इसे यहाँ देखेंगे; अन्यथा यह Quantfolio ऐप के ज़रिए आपके फ़ोन पर जाएगी।",
    saveFailed: "सूचना सेटिंग सहेजी नहीं जा सकी",
    loadFailed: "आपकी सूचना सेटिंग्स लोड नहीं हो सकीं",
    events: {
      signatureRequested: {
        label: "हस्ताक्षर के लिए भेजा गया",
        description: "आपका कोई फ़्लो हस्ताक्षर के लिए क्लाइंट को भेजा गया।",
      },
      signed: { label: "हस्ताक्षरित", description: "क्लाइंट ने आपके किसी फ़्लो पर हस्ताक्षर किए।" },
      signatureRejected: {
        label: "हस्ताक्षर अस्वीकार",
        description: "क्लाइंट ने आपके किसी फ़्लो पर हस्ताक्षर करने से इनकार किया।",
      },
      signatureExpired: {
        label: "हस्ताक्षर की समय-सीमा समाप्त",
        description: "आपके किसी फ़्लो का हस्ताक्षर अनुरोध हस्ताक्षर से पहले समाप्त हो गया।",
      },
      submittedByClient: {
        label: "क्लाइंट द्वारा सबमिट",
        description: "क्लाइंट ने अपने लिंक से आपका कोई फ़्लो सबमिट किया।",
      },
      completed: { label: "फ़्लो पूरा हुआ", description: "आपका कोई फ़्लो पूरा हुआ।" },
      complianceDecided: {
        label: "अनुपालन निर्णय",
        description: "अनुपालन ने आपके किसी फ़्लो को स्वीकृत या अस्वीकृत किया।",
      },
      supportReply: {
        label: "सहायता के जवाब",
        description: "आपके बनाए किसी सहायता टिकट पर किसी ने जवाब दिया।",
      },
    },
  },
  zh_CN: {
    title: "通知",
    description:
      "选择您希望收到通知的事项。Quantfolio 打开时会在此处显示；否则会通过 Quantfolio 应用发送到您的手机。",
    saveFailed: "无法保存通知设置",
    loadFailed: "无法加载您的通知设置",
    events: {
      signatureRequested: {
        label: "已发送签署",
        description: "您的某个流程已发送给客户签署。",
      },
      signed: { label: "已签署", description: "客户已签署您的某个流程。" },
      signatureRejected: { label: "拒绝签署", description: "客户拒绝签署您的某个流程。" },
      signatureExpired: {
        label: "签署已过期",
        description: "您的某个流程的签署请求在签署前已过期。",
      },
      submittedByClient: {
        label: "客户已提交",
        description: "客户通过其链接提交了您的某个流程。",
      },
      completed: { label: "流程已完成", description: "您的某个流程已完成。" },
      complianceDecided: {
        label: "合规决定",
        description: "合规已批准或拒绝您的某个流程。",
      },
      supportReply: { label: "支持回复", description: "有人回复了您创建的支持工单。" },
    },
  },
  zh_TW: {
    title: "通知",
    description:
      "選擇您希望收到通知的事項。Quantfolio 開啟時會在此處顯示；否則會透過 Quantfolio 應用程式傳送到您的手機。",
    saveFailed: "無法儲存通知設定",
    loadFailed: "無法載入您的通知設定",
    events: {
      signatureRequested: {
        label: "已傳送簽署",
        description: "您的某個流程已傳送給客戶簽署。",
      },
      signed: { label: "已簽署", description: "客戶已簽署您的某個流程。" },
      signatureRejected: { label: "拒絕簽署", description: "客戶拒絕簽署您的某個流程。" },
      signatureExpired: {
        label: "簽署已過期",
        description: "您的某個流程的簽署請求在簽署前已過期。",
      },
      submittedByClient: {
        label: "客戶已提交",
        description: "客戶透過其連結提交了您的某個流程。",
      },
      completed: { label: "流程已完成", description: "您的某個流程已完成。" },
      complianceDecided: {
        label: "法遵決定",
        description: "法遵已核准或駁回您的某個流程。",
      },
      supportReply: { label: "支援回覆", description: "有人回覆了您建立的支援工單。" },
    },
  },
  nl_NL: {
    title: "Meldingen",
    description:
      "Kies waarover je een melding wilt krijgen. Zolang Quantfolio open is zie je het hier; anders gaat het via de Quantfolio-app naar je telefoon.",
    saveFailed: "De meldingsinstelling kon niet worden opgeslagen",
    loadFailed: "Je meldingsinstellingen konden niet worden geladen",
    events: {
      signatureRequested: {
        label: "Ter ondertekening verstuurd",
        description: "Een van je flows wordt ter ondertekening naar de klant gestuurd.",
      },
      signed: { label: "Ondertekend", description: "De klant heeft een van je flows ondertekend." },
      signatureRejected: {
        label: "Ondertekening geweigerd",
        description: "De klant heeft geweigerd een van je flows te ondertekenen.",
      },
      signatureExpired: {
        label: "Ondertekening verlopen",
        description:
          "Een ondertekeningsverzoek voor een van je flows is verlopen voordat het werd ondertekend.",
      },
      submittedByClient: {
        label: "Ingediend door de klant",
        description: "De klant heeft een van je flows via zijn link ingediend.",
      },
      completed: { label: "Flow voltooid", description: "Een van je flows is voltooid." },
      complianceDecided: {
        label: "Compliancebesluit",
        description: "Compliance heeft een van je flows goedgekeurd of afgewezen.",
      },
      supportReply: {
        label: "Antwoorden van support",
        description: "Iemand heeft gereageerd op een supportticket dat jij hebt aangemaakt.",
      },
    },
  },
  nb_NO: {
    title: "Varsler",
    description:
      "Velg hva du vil bli varslet om. Når Quantfolio er åpent, ser du det her; ellers går varselet til telefonen din gjennom Quantfolio-appen.",
    saveFailed: "Kunne ikke lagre varslingsinnstillingen",
    loadFailed: "Kunne ikke laste varslingsinnstillingene dine",
    events: {
      signatureRequested: {
        label: "Sendt til signering",
        description: "En av flytene dine sendes til kunden for signering.",
      },
      signed: { label: "Signert", description: "Kunden har signert en av flytene dine." },
      signatureRejected: {
        label: "Signering avvist",
        description: "Kunden avviste å signere en av flytene dine.",
      },
      signatureExpired: {
        label: "Signering utløpt",
        description: "En signeringsforespørsel for en av flytene dine utløp før den ble signert.",
      },
      submittedByClient: {
        label: "Sendt inn av kunden",
        description: "Kunden sendte inn en av flytene dine fra lenken sin.",
      },
      completed: { label: "Flyt fullført", description: "En av flytene dine er fullført." },
      complianceDecided: {
        label: "Compliance-beslutning",
        description: "Compliance godkjente eller avviste en av flytene dine.",
      },
      supportReply: {
        label: "Svar fra support",
        description: "Noen svarte på en supportsak du opprettet.",
      },
    },
  },
  sv_SE: {
    title: "Aviseringar",
    description:
      "Välj vad du vill bli aviserad om. När Quantfolio är öppet ser du det här; annars skickas det till din telefon via Quantfolio-appen.",
    saveFailed: "Det gick inte att spara aviseringsinställningen",
    loadFailed: "Det gick inte att läsa in dina aviseringsinställningar",
    events: {
      signatureRequested: {
        label: "Skickat för signering",
        description: "Ett av dina flöden skickas till kunden för signering.",
      },
      signed: { label: "Signerat", description: "Kunden har signerat ett av dina flöden." },
      signatureRejected: {
        label: "Signering avböjd",
        description: "Kunden avböjde att signera ett av dina flöden.",
      },
      signatureExpired: {
        label: "Signering har gått ut",
        description: "En signeringsbegäran för ett av dina flöden gick ut innan den signerades.",
      },
      submittedByClient: {
        label: "Inskickat av kunden",
        description: "Kunden skickade in ett av dina flöden via sin länk.",
      },
      completed: { label: "Flöde slutfört", description: "Ett av dina flöden är slutfört." },
      complianceDecided: {
        label: "Compliancebeslut",
        description: "Compliance godkände eller avvisade ett av dina flöden.",
      },
      supportReply: {
        label: "Svar från support",
        description: "Någon svarade på ett supportärende som du skapade.",
      },
    },
  },
  fi_FI: {
    title: "Ilmoitukset",
    description:
      "Valitse, mistä haluat ilmoituksen. Kun Quantfolio on auki, näet sen täällä; muuten se lähetetään puhelimeesi Quantfolio-sovelluksen kautta.",
    saveFailed: "Ilmoitusasetusta ei voitu tallentaa",
    loadFailed: "Ilmoitusasetuksiasi ei voitu ladata",
    events: {
      signatureRequested: {
        label: "Lähetetty allekirjoitettavaksi",
        description: "Yksi prosesseistasi lähetetään asiakkaalle allekirjoitettavaksi.",
      },
      signed: {
        label: "Allekirjoitettu",
        description: "Asiakas on allekirjoittanut yhden prosesseistasi.",
      },
      signatureRejected: {
        label: "Allekirjoitus hylätty",
        description: "Asiakas kieltäytyi allekirjoittamasta yhtä prosesseistasi.",
      },
      signatureExpired: {
        label: "Allekirjoitus vanheni",
        description: "Yhden prosessisi allekirjoituspyyntö vanheni ennen allekirjoitusta.",
      },
      submittedByClient: {
        label: "Asiakkaan lähettämä",
        description: "Asiakas lähetti yhden prosesseistasi linkkinsä kautta.",
      },
      completed: { label: "Prosessi valmis", description: "Yksi prosesseistasi on valmis." },
      complianceDecided: {
        label: "Compliance-päätös",
        description: "Compliance hyväksyi tai hylkäsi yhden prosesseistasi.",
      },
      supportReply: {
        label: "Tuen vastaukset",
        description: "Joku vastasi luomaasi tukipyyntöön.",
      },
    },
  },
  da_DK: {
    title: "Notifikationer",
    description:
      "Vælg, hvad du vil have besked om. Når Quantfolio er åbent, ser du det her; ellers sendes det til din telefon via Quantfolio-appen.",
    saveFailed: "Notifikationsindstillingen kunne ikke gemmes",
    loadFailed: "Dine notifikationsindstillinger kunne ikke indlæses",
    events: {
      signatureRequested: {
        label: "Sendt til underskrift",
        description: "Et af dine forløb sendes til kunden til underskrift.",
      },
      signed: { label: "Underskrevet", description: "Kunden har underskrevet et af dine forløb." },
      signatureRejected: {
        label: "Underskrift afvist",
        description: "Kunden afviste at underskrive et af dine forløb.",
      },
      signatureExpired: {
        label: "Underskrift udløbet",
        description:
          "En anmodning om underskrift på et af dine forløb udløb, før den blev underskrevet.",
      },
      submittedByClient: {
        label: "Indsendt af kunden",
        description: "Kunden indsendte et af dine forløb fra sit link.",
      },
      completed: { label: "Forløb gennemført", description: "Et af dine forløb er gennemført." },
      complianceDecided: {
        label: "Compliance-beslutning",
        description: "Compliance godkendte eller afviste et af dine forløb.",
      },
      supportReply: {
        label: "Svar fra support",
        description: "Nogen svarede på en supportsag, du har oprettet.",
      },
    },
  },
  is_IS: {
    title: "Tilkynningar",
    description:
      "Veldu um hvað þú vilt fá tilkynningu. Þegar Quantfolio er opið sérðu það hér; annars fer það í símann þinn í gegnum Quantfolio-appið.",
    saveFailed: "Ekki tókst að vista tilkynningastillinguna",
    loadFailed: "Ekki tókst að hlaða tilkynningastillingunum þínum",
    events: {
      signatureRequested: {
        label: "Sent til undirritunar",
        description: "Eitt af ferlunum þínum er sent til viðskiptavinar til undirritunar.",
      },
      signed: {
        label: "Undirritað",
        description: "Viðskiptavinur hefur undirritað eitt af ferlunum þínum.",
      },
      signatureRejected: {
        label: "Undirritun hafnað",
        description: "Viðskiptavinur hafnaði að undirrita eitt af ferlunum þínum.",
      },
      signatureExpired: {
        label: "Undirritun útrunnin",
        description:
          "Beiðni um undirritun á einu af ferlunum þínum rann út áður en hún var undirrituð.",
      },
      submittedByClient: {
        label: "Sent inn af viðskiptavini",
        description: "Viðskiptavinur sendi inn eitt af ferlunum þínum úr hlekknum sínum.",
      },
      completed: { label: "Ferli lokið", description: "Einu af ferlunum þínum er lokið." },
      complianceDecided: {
        label: "Ákvörðun regluvörslu",
        description: "Regluvarsla samþykkti eða hafnaði einu af ferlunum þínum.",
      },
      supportReply: {
        label: "Svör frá þjónustu",
        description: "Einhver svaraði þjónustubeiðni sem þú stofnaðir.",
      },
    },
  },
};
